<template>
  <div class="member_usr_info_modal_main">
    <div class="member_usr_info_modal">
      <div class="member_usr_info_modal_center">
        <div class="member_usr_info_modal_body">
          <span class="member_usr_popup_close" @click="closeMemInfoPopupMethod">
            <i class="fa fa-times-circle-o" aria-hidden="true"></i>
          </span>

          <div class="row" v-if="userInfo">
            <div class="col-md-auto custom_width">
              <div class="user_basic_info">
                <div class="member_usr_avtaar_info">
                  <div class="prfl_avatar_lazy_load_box" v-if="userProfile.thumbnail || userProfile.avatar">
                    <!-- Low-resolution initial placeholder image -->
                    <img 
                      v-if="userProfile.thumbnail"
                      :src="userProfile.thumbnail" 
                      :alt="userDisplayName" 
                      class="img-fluid thumb-placeholder"
                      :class="{ 'fade-out': isFullAvatarLoaded }"
                    >

                    <!-- Full-resolution quality image -->
                    <img 
                      v-if="userProfile.avatar"
                      :src="userProfile.avatar" 
                      :alt="userDisplayName" 
                      class="img-fluid full-avatar"
                      :class="{ 'fade-in': isFullAvatarLoaded }"
                      @load="onFullAvatarLoad"
                    >
                  </div>
                  <img 
                    v-else 
                    src="@/assets/images/user_default.png" 
                    alt="Default User" 
                    class="img-fluid"
                  >
                </div>

                <div class="member_usr_info_list">
                  <div class="member_usr_info_label" v-if="userProfile.designation">
                    <div class="member_usr_info_label_key">
                      <img src="@/assets/images/info_icon/designation_icon_red.png" class="img-fluid" alt="Designation">
                    </div>
                    <div class="member_usr_info_label_value">{{ userProfile.designation }}</div>
                  </div>

                  <div class="member_usr_info_label" v-if="userProfile.area_of_work">
                    <div class="member_usr_info_label_key">
                      <img src="@/assets/images/info_icon/organisation_ico.png" class="img-fluid" alt="Organization">
                    </div>
                    <div class="member_usr_info_label_value">{{ userProfile.area_of_work }}</div>
                  </div>

                  <div class="member_usr_info_label" v-if="userProfile.department">
                    <div class="member_usr_info_label_key">
                      <img src="@/assets/images/info_icon/depart_icon.png" class="img-fluid depart_icon" alt="Department">
                    </div>
                    <div class="member_usr_info_label_value">{{ userProfile.department }}</div>
                  </div>

                  <div class="member_usr_info_label" v-if="userInfoExtraData && userInfoExtraData.email">
                    <div class="member_usr_info_label_key">
                      <img src="@/assets/images/info_icon/mail_icon.png" class="img-fluid" alt="Email">
                    </div>
                    <div class="member_usr_info_label_value">{{ userInfoExtraData.email }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col">
              <div class="mam_usr_main_info">
                <div class="mem_usr_name_info">
                  <h5 class="usr_name">{{ userDisplayName }}</h5>
                  <div class="member_user_d_country" v-if="userProfile.country || userProfile.city">
                    <span v-if="userProfile.city">{{ userProfile.city }}, </span>
                    <template v-if="userProfile.country">
                      <span>{{ getCountryName(userProfile.country) }}</span>
                      <img 
                        :src="getCountryFlag(userProfile.country)" 
                        class="country-flag ml-2" 
                        alt="Flag" 
                      />
                    </template>
                  </div>
                </div>

                <div class="mem_usr_about_me_info">
                  <h6>About Me</h6>
                  <div 
                    v-if="userBio" 
                    v-html="userBio"
                  ></div>
                  <p v-else>
                    I am an AI Enthusiast and am here to discover how the worlds of AI and ML can significantly assist Precision Medicine. I am ready to expand my intellectual horizons and research skill set.
                  </p>
                </div>

                <div class="mem_usr_action_btn">
                  <!-- Accessible CV Download -->
                  <a 
                    v-if="hasAccessibleCv" 
                    :href="userCvData.cv" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="btn btn-primary cv_btn mr-3"
                  >
                    CV
                  </a>

                  <!-- Inaccessible CV Tooltip -->
                  <span v-else class="cv_tooltip_popup">
                    <button type="button" class="btn btn-primary cv_btn mr-3">CV</button>
                    <div class="todo_tooltip_text">
                      This member has not made their CV accessible yet.
                    </div>
                  </span>

                  <label class="btn btn-success connection_connected_btn connected mb-0">
                    Connected
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bus } from '@/main';
import countries from '@/services/country.json';

// Pre-index country map for constant-time lookup
const COUNTRY_MAP = {};
if (countries && typeof countries === 'object') {
  Object.keys(countries).forEach((code) => {
    COUNTRY_MAP[code.toLowerCase()] = String(countries[code]).toLowerCase();
  });
}

export default {
  name: 'connected-user-info',
  props: {
    memUsrInfo: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      userInfoExtraData: null,
      userCvData: null,
      cvButtonCheck: false,
      isFullAvatarLoaded: false
    };
  },
  computed: {
    userInfo() {
      return this.memUsrInfo || null;
    },
    userProfile() {
      return (this.userInfo && this.userInfo.profile) || {};
    },
    userDisplayName() {
      if (!this.userInfo) return '';
      const parts = [this.userInfo.salutation, this.userInfo.first_name, this.userInfo.last_name];
      return parts.filter(Boolean).join(' ');
    },
    userBio() {
      return this.userInfoExtraData && this.userInfoExtraData.profile && this.userInfoExtraData.profile.bio;
    },
    hasAccessibleCv() {
      return !!(this.cvButtonCheck && this.userCvData && this.userCvData.pdf_cv_uploaded && this.userCvData.cv);
    }
  },
  watch: {
    'memUsrInfo.id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.isFullAvatarLoaded = false;
          this.fetchExtraDetails(newId);
        }
      }
    }
  },
  methods: {
    onFullAvatarLoad() {
      this.isFullAvatarLoaded = true;
    },
    fetchExtraDetails(userId) {
      this.$store.dispatch('getMemberUserDetailsFun', { id: userId })
        .then((res) => {
          this.userInfoExtraData = (res && res.data && res.data.data) || null;
        })
        .catch(() => {
          this.userInfoExtraData = null;
        });

      this.$store.dispatch('getCVbyUserIdAction', { id: userId })
        .then((res) => {
          this.userCvData = (res && res.data && res.data.data) || null;
          this.cvButtonCheck = true;
        })
        .catch((err) => {
          this.userCvData = (err && err.data) || null;
          this.cvButtonCheck = false;
        });
    },
    closeMemInfoPopupMethod() {
      bus.$emit('memUsrInfoPopupStateBus', false);
      document.body.classList.remove('notConnectedUserBody');
    },
    getCountryName(countryCode) {
      if (!countryCode) return '';
      const code = String(countryCode).toLowerCase();
      return COUNTRY_MAP[code] || countryCode;
    },
    getCountryFlag(countryCode) {
      const code = (countryCode || 'IN').toUpperCase();
      try {
        return require(`@/assets/images/countries/${code}.svg`);
      } catch (e) {
        return require('@/assets/images/countries/IN.svg');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.member_usr_info_modal_main {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 1000;
  height: 100vh;
  overflow-y: auto;

  .member_usr_info_modal {
    display: table;
    height: 100%;
    width: 100%;

    .member_usr_info_modal_center {
      display: table-cell;
      vertical-align: middle;
    }
  }

  .member_usr_info_modal_body {
    background: #000;
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    border-radius: 5px;
    padding: 20px 25px;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    color: #ffffff;
    box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    position: relative;

    .member_usr_avtaar_info {
      margin-bottom: 15px;
      min-height: 280px;
      min-width: 280px;

      .img-fluid {
        width: 100%;
      }
    }

    .prfl_avatar_lazy_load_box {
      position: relative;
      width: 280px;
      min-height: 280px;
      overflow: hidden;

      .img-fluid {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transform: scale(1);
        transition: transform ease 0.5s, opacity 0.4s ease-in-out;
        backface-visibility: hidden;
      }

      .thumb-placeholder {
        filter: blur(8px);
        opacity: 1;
        z-index: 1;

        &.fade-out {
          opacity: 0;
          pointer-events: none;
        }
      }

      .full-avatar {
        opacity: 0;
        z-index: 2;

        &.fade-in {
          opacity: 1;
        }
      }

      &:hover .img-fluid {
        transform: scale(1.1);
      }
    }

    .member_usr_info_list {
      margin-top: 10px;
    }

    .member_usr_info_label {
      display: flex;
      align-items: flex-start;
      font-size: 18px;
      margin-bottom: 12px;

      .member_usr_info_label_key {
        width: 50px;
        flex: 0 0 50px;
        color: #ffe500;

        .img-fluid {
          max-width: 30px;

          &.depart_icon {
            max-width: 28px;
          }
        }
      }

      .member_usr_info_label_value {
        font-size: 18px;
        word-break: break-word;
        color: #d6d6d6;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .mam_usr_main_info {
      .usr_name {
        font-size: 28px;
        color: #ffe500;
        margin: 0 0 5px;
        line-height: 1.3;
        font-family: 'BeonMedium', sans-serif;

        @media only screen and (max-width: 767px) {
          font-size: 24px;
          margin-top: 15px;
        }
      }

      .member_user_d_country {
        min-height: 40px;

        span {
          font-size: 16px;
        }

        .country-flag {
          width: 40px;
        }
      }

      .mem_usr_about_me_info {
        border: 1px solid #ff0000;
        padding: 12px 15px;
        margin: 10px 0;
        min-height: 150px;

        h6 {
          font-size: 18px;
        }

        div {
          font-size: 15px;
          text-align: justify;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .mem_usr_action_btn {
        padding-top: 20px;
        text-align: right;

        .btn {
          min-width: 120px;
          border-radius: 30px;
          box-shadow: none;
          padding: 9px 20px;
          line-height: 1;
          border: none;
          font-family: 'MyriadProRegular', 'Lato', sans-serif;
          font-size: 17px;

          &.connection_connected_btn.connected {
            background: #28a745;
            color: #ffffff;
            cursor: default;

            &:hover {
              background: #28a745;
              color: #ffffff;
            }
          }

          &.cv_btn {
            background: transparent !important;
            border: 2px solid #ffd400 !important;
            color: #ffd400;
            letter-spacing: 1.5px;
            padding: 8px 20px;

            &:hover {
              background: #ffd400 !important;
              color: #000000 !important;
            }
          }
        }
      }
    }

    .member_usr_popup_close {
      position: absolute;
      top: 5px;
      right: 5px;
      line-height: 1;
      z-index: 100;

      .fa {
        font-size: 30px;
        color: #e4002b;
        cursor: pointer;
        transition: all ease 0.5s;

        &:hover {
          color: #87cefa;
        }
      }
    }
  }

  @media only screen and (min-width: 1023px) {
    .custom_width {
      width: 320px;
    }
  }

  .cv_tooltip_popup {
    position: relative;
    display: inline-block;

    .todo_tooltip_text {
      right: 0;
      position: absolute;
      background: #ffd400;
      width: 410px;
      padding: 2px 5px;
      cursor: default;
      line-height: 1.4;
      font-family: 'MyriadProRegular', 'Lato', sans-serif;
      font-size: 16px;
      color: #000;
      top: 37px;
      border-radius: 2px;
      display: none;
      text-align: center;
      z-index: 100;

      &:before {
        content: "";
        border: solid;
        border-color: #ffd400 transparent;
        border-width: 12px 12px 0 0;
        top: -5px;
        display: block;
        position: absolute;
        transform: rotate(45deg);
        right: 60px;
      }

      @media only screen and (max-width: 767px) {
        right: -100px;
        width: 280px;

        &:before {
          right: 160px;
        }
      }
    }

    &:hover .todo_tooltip_text {
      display: block;
    }
  }
}
</style>